import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner, PageFooter } from '../shared/components';
import { icon } from '../shared/components/icons';
import { infoIcon } from '../shared/components/InfoIcon';

// Guard a money total → "$1,234" or "—" when missing / non-finite.
const money = (v: unknown): string =>
  v != null && Number.isFinite(Number(v)) ? '$' + Number(v).toLocaleString() : '—';

export class BookingQuotePage {
  private container: HTMLElement;
  private listings: { id: string; name: string }[] = [];
  private formulas: any[] = [];
  private quoteData: any = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    const today = new Date();
    const fmt   = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const defaultIn  = fmt(new Date(today.getTime() + 7  * 86400000));
    const defaultOut = fmt(new Date(today.getTime() + 10 * 86400000));

    this.container.innerHTML = `
      <div class="page-content">

        <div class="dash-head">
          <h1>Booking Quote</h1>
          <p>What a guest would pay for a stay — broken down by fee type and compared across booking channels.</p>
        </div>

        <style>
          .bq-form { display:flex; flex-wrap:wrap; gap:12px; align-items:flex-end; padding:20px; background:var(--bg-card,#fff); border-radius:12px; border:1px solid var(--border); margin-bottom:20px; }
          .bq-field { display:flex; flex-direction:column; gap:5px; }
          .bq-field label { font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
          .bq-field select, .bq-field input { padding:9px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; color:var(--text); background:var(--bg,#fff); min-width:160px; }
          .bq-field select:focus, .bq-field input:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
          .bq-calc-btn { padding:9px 20px; background:var(--accent); color:#fff; border:none; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; white-space:nowrap; transition:background 0.15s; }
          .bq-calc-btn:hover { background:var(--accent-hover); }
          .bq-calc-btn:focus-visible { outline:none; box-shadow:var(--focus-ring); }
          .bq-calc-btn:disabled { background:#94a3b8; cursor:not-allowed; }

          .bq-results { display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:16px; margin-bottom:16px; }

          /* The result columns hold money tables — keep them overflow-safe. */
          .bq-result-grid { display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:16px; }
          @media (max-width:640px) {
            .bq-results, .bq-result-grid { grid-template-columns:1fr; }
            .bq-field { flex:1 1 100%; }
            .bq-field select, .bq-field input { min-width:0; width:100%; }
            .bq-calc-btn { width:100%; }
          }

          .bq-card { background:var(--bg-card,#fff); border:1px solid var(--border); border-radius:12px; padding:20px; }
          .bq-card-title { font-size:13px; font-weight:700; color:var(--text); margin-bottom:14px; display:flex; align-items:center; gap:8px; }

          .bq-line { display:flex; justify-content:space-between; align-items:baseline; padding:7px 0; font-size:13px; }
          .bq-line + .bq-line { border-top:1px solid #f1f5f9; }
          .bq-line.bq-total { border-top:2px solid var(--border) !important; margin-top:4px; padding-top:11px; font-weight:700; font-size:15px; }
          .bq-line-label { color:var(--text-muted); min-width:0; }
          .bq-line-val { font-weight:600; color:var(--text); white-space:nowrap; font-variant-numeric:tabular-nums; }
          .bq-unavail { display:inline-flex; align-items:center; gap:5px; padding:4px 10px; border-radius:6px; background:var(--danger-soft); color:var(--danger); font-size:11px; font-weight:600; margin-top:4px; }

.bq-channels { background:var(--bg-card,#fff); border:1px solid var(--border); border-radius:12px; overflow:hidden; margin-bottom:16px; }
          .bq-ch-header { padding:14px 20px; border-bottom:1px solid var(--border); }
          .bq-ch-title  { font-size:13px; font-weight:700; color:var(--text); }
          .bq-ch-sub    { font-size:11px; color:var(--text-muted); margin-top:2px; }
          .bq-ch-table  { width:100%; border-collapse:collapse; }
          .bq-ch-table th { padding:9px 16px; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em; background:#f8fafc; border-bottom:1px solid var(--border); text-align:right; }
          .bq-ch-table th:first-child { text-align:left; }
          .bq-ch-table td { padding:12px 16px; font-size:13px; border-bottom:1px solid #f8fafc; text-align:right; }
          .bq-ch-table td:first-child { text-align:left; }
          .bq-ch-table tr:last-child td { border-bottom:none; }
          .bq-ch-badge { display:inline-flex; align-items:center; gap:6px; font-weight:600; }
          .bq-ch-dot { width:9px; height:9px; border-radius:50%; flex-shrink:0; }
          .bq-best { background:var(--success-soft); }
          .bq-best td { color:var(--success-text); }

          .bq-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 20px; color:var(--text-muted); text-align:center; }
          .bq-empty-icon { font-size:48px; margin-bottom:12px; opacity:0.4; }
        </style>

        <!-- Input form -->
        <div class="bq-form">
          <div class="bq-field" style="flex:1; min-width:200px;">
            <label>Property</label>
            <select id="bq-listing">
              <option value="">Loading properties…</option>
            </select>
          </div>
          <div class="bq-field">
            <label>Check-in</label>
            <input type="date" id="bq-checkin" value="${defaultIn}" />
          </div>
          <div class="bq-field">
            <label>Check-out</label>
            <input type="date" id="bq-checkout" value="${defaultOut}" />
          </div>
          <div class="bq-field">
            <label>Guests</label>
            <input type="number" id="bq-guests" value="2" min="1" max="20" style="min-width:70px; max-width:80px;" />
          </div>
          <button class="bq-calc-btn" id="bq-calc-btn" disabled>Calculate</button>
        </div>

        <!-- Results area -->
        <div id="bq-results">
          <div class="bq-empty">
            <div class="bq-empty-icon">${icon('home', 44)}</div>
            <div style="font-size:14px; font-weight:500; margin-bottom:4px;">Select a property and dates</div>
            <div style="font-size:12px;">See exactly what a guest would pay, broken down by fee type and channel</div>
          </div>
        </div>

        ${PageFooter.render([
          { abbr: 'Base Rent',    full: 'Accommodation',       note: 'sum of PriceLabs per-night rates from our synced calendar for the selected dates' },
          { abbr: 'Cleaning Fee', full: 'Cleaning Fee',        note: 'median of the last 20 actual reservations for this property from our reservations data' },
          { abbr: 'Service Fee',  full: 'Channel Service Fee', note: 'fee charged to guests by the booking platform (Airbnb ~14%, VRBO ~6%, Direct 0%)' },
          { abbr: 'Tax',          full: 'Occupancy Tax',       note: 'effective rate derived from recent direct reservations for this property' },
        ])}

      </div>
    `;

    await Promise.all([this.loadListings(), this.loadFormulas()]);
    this.bindEvents();
    this.renderSavedQuotes();
  }

  // ── Load listings dropdown ─────────────────────────────────

  private async loadListings(): Promise<void> {
    try {
      const res  = await fetch('/api/listings/active');
      const data = await res.json();
      this.listings = (data as any[])
        .map(d => ({ id: d.id, name: d.name || '?' }))
        .filter(l => l.id)
        .sort((a, b) => a.name.localeCompare(b.name));

      const sel = document.getElementById('bq-listing') as HTMLSelectElement;
      if (!sel) return;
      sel.innerHTML = `<option value="">Select a property…</option>` +
        this.listings.map(l => `<option value="${l.id}">${Formatters.escapeHtml(l.name)}</option>`).join('');

      // Deep link from the Admin registry ("Quote…" on a property row).
      const pre = sessionStorage.getItem('quote_preselect');
      if (pre) {
        sessionStorage.removeItem('quote_preselect');
        if (this.listings.some(l => String(l.id) === String(pre))) sel.value = String(pre);
      }

      const btn = document.getElementById('bq-calc-btn') as HTMLButtonElement;
      if (btn) btn.disabled = !sel.value;
      sel.addEventListener('change', () => { if (btn) btn.disabled = !sel.value; });
    } catch (err) {
      console.error('Failed to load listings:', err);
    }
  }

  private async loadFormulas(): Promise<void> {
    try {
      const res = await fetch('/api/formulas');
      const data = await res.json();
      this.formulas = (data as any[]).filter(f => !f.effective_to);
    } catch (err) {
      console.error('Failed to load formulas:', err);
    }
  }

  // ── Events ─────────────────────────────────────────────────

  private bindEvents(): void {
    document.getElementById('bq-calc-btn')?.addEventListener('click', () => this.calculate());

    // Allow Enter key in any field to trigger calculate
    ['bq-listing','bq-checkin','bq-checkout','bq-guests'].forEach(id => {
      document.getElementById(id)?.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') this.calculate();
      });
    });

    // Auto-advance check-out when check-in changes
    document.getElementById('bq-checkin')?.addEventListener('change', () => {
      const ci = (document.getElementById('bq-checkin') as HTMLInputElement).value;
      const co = (document.getElementById('bq-checkout') as HTMLInputElement).value;
      if (ci && co && co <= ci) {
        const d = new Date(ci + 'T00:00:00');
        d.setDate(d.getDate() + 3);
        (document.getElementById('bq-checkout') as HTMLInputElement).value =
          `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      }
    });
  }

  // ── Calculate ──────────────────────────────────────────────

  private async calculate(): Promise<void> {
    const listingId = (document.getElementById('bq-listing')  as HTMLSelectElement).value;
    const checkIn   = (document.getElementById('bq-checkin')  as HTMLInputElement).value;
    const checkOut  = (document.getElementById('bq-checkout') as HTMLInputElement).value;
    const guests    = (document.getElementById('bq-guests')   as HTMLInputElement).value;
    const btn       = document.getElementById('bq-calc-btn')  as HTMLButtonElement;

    if (!listingId || !checkIn || !checkOut) return;
    if (checkOut <= checkIn) {
      this.showError('Check-out must be after check-in.');
      return;
    }

    const el = document.getElementById('bq-results')!;
    el.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    btn.disabled = true;
    btn.textContent = 'Calculating…';

    try {
      const res = await fetch(`/api/quote?listingId=${listingId}&checkIn=${checkIn}&checkOut=${checkOut}&guests=${guests}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this.renderResults(data);
    } catch (err: any) {
      this.showError(err.message || 'Failed to calculate quote.');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Calculate';
    }
  }

  // ── Render results ─────────────────────────────────────────

  private renderResults(data: any): void {
    const el = document.getElementById('bq-results');
    if (!el) return;

    this.quoteData = data;

    const { listing, params, channels, available } = data;
    const fmtD = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const unavailWarning = !available
      ? `<div class="bq-unavail">${icon('flag', 13)} Some nights are not available — prices shown for reference only</div>`
      : '';

    const wch = channels[0];
    const otherChannels = channels.slice(1); // Airbnb, VRBO, Booking.com

    el.innerHTML = `
      <div style="margin-bottom:16px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;">
        <div>
          <div style="font-size:16px;font-weight:700;color:var(--text);">${Formatters.escapeHtml(listing.name)}</div>
          <div style="font-size:12px;color:var(--text-muted);margin-top:2px;">
            ${Formatters.escapeHtml(fmtD(params.checkIn))} → ${Formatters.escapeHtml(fmtD(params.checkOut))}
            · ${params.nights} night${params.nights !== 1 ? 's' : ''}
            · ${params.guests} guest${params.guests !== 1 ? 's' : ''}
            · <a href="/api/quote/debug?listingId=${listing.id}&checkIn=${params.checkIn}&checkOut=${params.checkOut}" target="_blank" style="color:var(--accent);font-size:11px;">raw data ↗</a>
          </div>
          ${unavailWarning}
        </div>
        <div style="text-align:right; display:flex; flex-direction:column; align-items:flex-end; gap:8px;">
          <div>
            <div style="font-size:11px;color:var(--text-muted);">WCH Direct total</div>
            <div style="font-size:26px;font-weight:800;color:var(--accent);">${money(wch?.total)}</div>
          </div>
          <div style="display:flex; gap:8px;">
            <button id="bq-save-btn" class="btn btn-secondary btn-sm">${icon('file', 14)} Save</button>
            <button id="bq-share-btn" class="btn btn-primary btn-sm">${icon('rocket', 14)} Share</button>
          </div>
        </div>
      </div>

      <div class="bq-result-grid">

        <!-- Left: WCH + formula -->
        <div class="bq-card" style="border-top:3px solid var(--accent);">
          <div class="bq-card-title">
            <span class="bq-ch-dot" style="background:var(--accent);width:10px;height:10px;border-radius:50%;display:inline-block;margin-right:6px;"></span>
            WCH Direct
          </div>
          <div id="bq-wch-body">${this.renderWCH()}</div>
        </div>

        <!-- Right: other channels stacked -->
        <div style="display:flex;flex-direction:column;gap:12px;">
          ${otherChannels.map((c: any) => `
            <div class="bq-card" style="border-top:3px solid ${c.color};">
              <div class="bq-card-title">
                <span class="bq-ch-dot" style="background:${c.color};width:10px;height:10px;border-radius:50%;display:inline-block;margin-right:6px;"></span>
                ${Formatters.escapeHtml(c.channel)}
                ${c.note ? `<span style="font-size:10px;font-weight:400;color:var(--text-muted);margin-left:6px;">${Formatters.escapeHtml(c.note)}</span>` : ''}
              </div>
              ${this.renderChannelLines(c, params.nights)}
            </div>`).join('')}
        </div>

      </div>
    `;

    this.bindFormulaEvent();
    this.bindQuoteActions(data);
    this.renderSavedQuotes();
  }

  private buildQuoteText(data: any): string {
    const { listing, params, channels } = data;
    const wch = channels[0];
    const fmtD = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return [
      `📋 Booking Quote — ${listing.name}`,
      `📅 ${fmtD(params.checkIn)} → ${fmtD(params.checkOut)} (${params.nights} nights, ${params.guests} guests)`,
      ``,
      `💳 WCH Direct`,
      `  Accommodation: $${channels[0].accommodation.toLocaleString()}`,
      channels[0].cleaning_fee > 0 ? `  Cleaning fee:  $${channels[0].cleaning_fee.toLocaleString()}` : null,
      channels[0].taxes > 0 ? `  Taxes:         $${channels[0].taxes.toLocaleString()}` : null,
      `  ─────────────────────────────`,
      `  Total:         $${wch.total.toLocaleString()}`,
      ``,
      ...channels.slice(1).map((c: any) => `${c.channel}: $${c.total.toLocaleString()}`),
    ].filter(l => l !== null).join('\n');
  }

  private bindQuoteActions(data: any): void {
    const saveBtn  = document.getElementById('bq-save-btn');
    const shareBtn = document.getElementById('bq-share-btn');

    const shareLabel = `${icon('rocket', 14)} Share`;

    saveBtn?.addEventListener('click', () => {
      const saved: any[] = JSON.parse(localStorage.getItem('bq-saved-quotes') || '[]');
      const entry = {
        id: Date.now(),
        listing: data.listing.name,
        checkIn: data.params.checkIn,
        checkOut: data.params.checkOut,
        nights: data.params.nights,
        guests: data.params.guests,
        total: data.channels[0].total,
        savedAt: new Date().toISOString(),
      };
      saved.unshift(entry);
      localStorage.setItem('bq-saved-quotes', JSON.stringify(saved.slice(0, 20)));
      if (saveBtn) { saveBtn.textContent = '✓ Saved'; (saveBtn as HTMLButtonElement).disabled = true; }
      this.renderSavedQuotes();
    });

    shareBtn?.addEventListener('click', () => {
      const text = this.buildQuoteText(data);
      navigator.clipboard.writeText(text).then(() => {
        if (shareBtn) shareBtn.textContent = '✓ Copied!';
        setTimeout(() => { if (shareBtn) shareBtn.innerHTML = shareLabel; }, 2000);
      });
    });
  }

  private renderSavedQuotes(): void {
    const saved: any[] = JSON.parse(localStorage.getItem('bq-saved-quotes') || '[]');
    let panel = document.getElementById('bq-saved-panel');
    if (!panel) {
      const resultsEl = document.getElementById('bq-results');
      if (!resultsEl) return;
      panel = document.createElement('div');
      panel.id = 'bq-saved-panel';
      resultsEl.parentNode?.insertBefore(panel, resultsEl.nextSibling);
    }
    if (!saved.length) { panel.innerHTML = ''; return; }
    const fmtD = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    panel.innerHTML = `
      <div style="background:var(--card); border:1px solid var(--border); border-radius:var(--radius-lg); padding:20px; margin-top:16px;">
        <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:12px;">
          <div style="font-size:13px; font-weight:700; color:var(--text); display:flex; align-items:center; gap:6px;">${icon('file', 15)} Saved Quotes (${saved.length})</div>
          <button id="bq-clear-saved" class="btn btn-sm btn-ghost">Clear all</button>
        </div>
        <div style="display:flex; flex-direction:column; gap:8px;">
          ${saved.map(q => `
            <div style="display:flex; align-items:center; justify-content:space-between; gap:12px; padding:10px 12px; background:var(--bg-subtle); border-radius:8px; font-size:13px;">
              <div style="min-width:0;">
                <div style="font-weight:600; color:var(--text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${Formatters.escapeHtml(q.listing)}</div>
                <div style="font-size:11px; color:var(--text-muted);">${fmtD(q.checkIn)} → ${fmtD(q.checkOut)} · ${q.nights} nights · ${q.guests} guests</div>
              </div>
              <div style="font-size:16px; font-weight:800; color:var(--accent); white-space:nowrap;">${money(q.total)}</div>
            </div>`).join('')}
        </div>
      </div>`;
    document.getElementById('bq-clear-saved')?.addEventListener('click', () => {
      localStorage.removeItem('bq-saved-quotes');
      this.renderSavedQuotes();
    });
  }

  private renderWCH(formulaCode?: string): string {
    if (!this.quoteData) return '';
    const { channels, params } = this.quoteData;
    const c = channels[0];
    const fmt$ = (v: number) => v > 0 ? '$' + v.toLocaleString() : '—';

    const selectedCode = formulaCode || (this.formulas[0]?.formula_code);
    const formula = this.formulas.find(f => f.formula_code === selectedCode) || this.formulas[0];

    // WCH fee is added ON TOP of rent, not deducted from owner
    const includesClean = formula?.formula_code.includes('INCLUDE_CLEAN');
    const commBase = formula ? (includesClean ? c.accommodation + c.cleaning_fee : c.accommodation) : 0;
    const wchFee   = formula ? Math.round(commBase * formula.commission_rate) : 0;

    // Guest total = accommodation + wch fee (on top) + cleaning + taxes
    const subtotal   = c.accommodation + wchFee + c.cleaning_fee;
    const guestTotal = subtotal + c.taxes;

    // Owner keeps the full nightly rate — WCH fee is charged to guest, not deducted from owner
    const ownerPayout = c.accommodation;

    const formulaOptions = this.formulas.map(f =>
      `<option value="${f.formula_code}" ${f.formula_code === selectedCode ? 'selected' : ''}>${Formatters.escapeHtml(f.formula_name)}</option>`
    ).join('');

    return `
      ${this.formulas.length > 0 ? `
      <div style="margin-bottom:12px;">
        <label style="font-size:11px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em;">Formula</label>
        <select id="bq-formula-sel" style="display:block;width:100%;margin-top:4px;padding:8px 10px;border:1px solid var(--border);border-radius:8px;font-size:12px;color:var(--text);background:var(--bg,#fff);">
          ${formulaOptions}
        </select>
      </div>` : ''}

      <div style="font-size:11px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px;">Guest Breakdown</div>
      <div class="bq-line"><span class="bq-line-label">Accommodation (${params.nights} nights)</span><span class="bq-line-val">${fmt$(c.accommodation)}</span></div>
      ${wchFee > 0 ? `<div class="bq-line"><span class="bq-line-label">WCH management fee (${Math.round(formula.commission_rate * 100)}%) ${infoIcon('management_fee')}</span><span class="bq-line-val">+${fmt$(wchFee)}</span></div>` : ''}
      ${c.cleaning_fee > 0 ? `<div class="bq-line"><span class="bq-line-label">Cleaning fee</span><span class="bq-line-val">${fmt$(c.cleaning_fee)}</span></div>` : ''}
      <div class="bq-line" style="border-top:1px solid #f1f5f9;padding-top:8px;margin-top:4px;">
        <span class="bq-line-label">Subtotal</span><span class="bq-line-val">${fmt$(subtotal)}</span>
      </div>
      ${c.taxes > 0 ? `<div class="bq-line"><span class="bq-line-label">Taxes (${Number.isFinite(Number(c.tax_rate)) ? c.tax_rate.toFixed(1) : '—'}%)</span><span class="bq-line-val">${fmt$(c.taxes)}</span></div>` : ''}
      <div class="bq-line bq-total"><span>Guest Total ${infoIcon('all_in_price')}</span><span style="color:var(--accent);">${money(guestTotal)}</span></div>

      <div style="margin-top:18px;padding-top:16px;border-top:2px solid var(--border);">
        <div style="font-size:11px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px;">Owner Payout ${infoIcon('owner_net')}</div>
        <div class="bq-line"><span class="bq-line-label">Base accommodation</span><span class="bq-line-val">${fmt$(c.accommodation)}</span></div>
        ${wchFee > 0 ? `<div class="bq-line"><span class="bq-line-label" style="color:var(--text-muted);">WCH management fee (charged to guest)</span><span class="bq-line-val" style="color:var(--text-muted);">not deducted</span></div>` : ''}
        ${c.cleaning_fee > 0 ? `<div class="bq-line"><span class="bq-line-label" style="color:var(--text-muted);">Cleaning (passthrough to WCH ops)</span><span class="bq-line-val" style="color:var(--text-muted);">−${fmt$(c.cleaning_fee)}</span></div>` : ''}
        ${c.taxes > 0 ? `<div class="bq-line"><span class="bq-line-label" style="color:var(--text-muted);">Taxes (bypass to jurisdiction)</span><span class="bq-line-val" style="color:var(--text-muted);">−${fmt$(c.taxes)}</span></div>` : ''}
        <div class="bq-line bq-total"><span>Owner Receives</span><span style="color:var(--success);">${money(ownerPayout)}</span></div>
      </div>
    `;
  }

  private renderChannelLines(c: any, nights: number): string {
    const fmt$ = (v: number) => v > 0 ? '$' + v.toLocaleString() : '—';
    const subtotal = c.accommodation + c.cleaning_fee + c.service_fee;
    return `
      <div class="bq-line"><span class="bq-line-label">Accommodation (${nights} nights)</span><span class="bq-line-val">${fmt$(c.accommodation)}</span></div>
      ${c.cleaning_fee > 0 ? `<div class="bq-line"><span class="bq-line-label">Cleaning fee</span><span class="bq-line-val">${fmt$(c.cleaning_fee)}</span></div>` : ''}
      ${c.service_fee > 0 ? `<div class="bq-line"><span class="bq-line-label">Service fee (${c.service_fee_pct}%) ${infoIcon('channel_fees')}</span><span class="bq-line-val">${fmt$(c.service_fee)}</span></div>` : ''}
      <div class="bq-line" style="border-top:1px solid #f1f5f9;padding-top:8px;margin-top:4px;">
        <span class="bq-line-label">Subtotal</span><span class="bq-line-val">${fmt$(subtotal)}</span>
      </div>
      ${c.taxes > 0 ? `<div class="bq-line"><span class="bq-line-label">Taxes (${Number.isFinite(Number(c.tax_rate)) ? c.tax_rate.toFixed(1) : '—'}%)</span><span class="bq-line-val">${fmt$(c.taxes)}</span></div>` : ''}
      <div class="bq-line bq-total"><span>Guest Total ${infoIcon('all_in_price')}</span><span style="font-weight:700;color:var(--text);">${money(c.total)}</span></div>
    `;
  }

  private bindFormulaEvent(): void {
    const sel = document.getElementById('bq-formula-sel') as HTMLSelectElement | null;
    if (!sel) return;
    sel.addEventListener('change', () => {
      const body = document.getElementById('bq-wch-body');
      if (body) body.innerHTML = this.renderWCH(sel.value);
      this.bindFormulaEvent();
    });
  }

  // ── Error state ────────────────────────────────────────────

  private showError(msg: string): void {
    const el = document.getElementById('bq-results');
    if (!el) return;
    el.innerHTML = `
      <div class="bq-empty">
        <div class="bq-empty-icon">${icon('flag', 44)}</div>
        <div style="font-size:14px; font-weight:500; margin-bottom:4px; color:var(--danger);">Could not calculate quote</div>
        <div style="font-size:12px;">${Formatters.escapeHtml(msg)}</div>
      </div>`;
  }
}
